/**
 * Layer 1 atoms — per-item via `atomFamily`, which is what keeps progress cheap.
 *
 * A throttled progress tick writes only `uploadItemFamily(id)`, so only that row re-renders.
 * Everything the panel and the engine read is derived through the primitive phase counts
 * below: Jotai only recomputes a derived atom when a dependency's *value* changed, so a tick
 * that leaves every count alone never reaches the header, the summary or the scheduler. That
 * is also why the summary is built out of numbers rather than reading the items directly — a
 * derived atom returning a fresh object would notify on every tick even when nothing moved.
 *
 * These live in the resettable `storeV2` store, so an org switch starts from an empty queue.
 */
import { atom, type Getter } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { ACTIVE_PHASES, CLIENT_BOUND_PHASES, UPLOAD_PANEL_STATE, UPLOAD_PHASE } from '@studio/constants/library-upload';
import type { LibraryUpload } from '@studio/types';

/** membership + stable display order (oldest first) */
export const uploadIdsAtom = atom<string[]>([]);

/** one atom per queue id — the unit of render isolation */
export const uploadItemFamily = atomFamily((_id: string) =>
  atom<LibraryUpload.Derived.UploadItem | undefined>(undefined)
);

/** panel UI state */
export const uploadPanelAtom = atom<{ minimized: boolean; open: boolean }>({ minimized: false, open: false });

/** Read per attempt by `use-upload-engine`, so a change leaves in-flight uploads alone. */
export const uploadSettingsAtom = atom<LibraryUpload.Derived.UploadSettings>({
  detectQrCodes: false,
  regionId: null,
});

const ACTIVE_PHASE_LIST = [...ACTIVE_PHASES];
const CLIENT_BOUND_PHASE_LIST = [...CLIENT_BOUND_PHASES];

const allItems = (get: Getter): LibraryUpload.Derived.UploadItem[] =>
  get(uploadIdsAtom)
    .map((id) => get(uploadItemFamily(id)))
    .filter((item): item is LibraryUpload.Derived.UploadItem => item !== undefined);

/**
 * How many items sit in one phase. The only place the item atoms are read in bulk, and a
 * number, so everything downstream is insulated from progress ticks.
 */
const phaseCountAtom = atomFamily((phase: LibraryUpload.Derived.UploadPhase) =>
  atom((get) => allItems(get).filter((item) => item.phase === phase).length)
);

export const totalCountAtom = atom((get) => get(uploadIdsAtom).length);

export const activeCountAtom = atom((get) =>
  ACTIVE_PHASE_LIST.reduce((total, phase) => total + get(phaseCountAtom(phase)), 0)
);

export const hasActiveUploadsAtom = atom((get) => get(activeCountAtom) > 0);

/**
 * Items whose work leaving the page would lose. Processing is excluded deliberately — the backend
 * finishes it either way. This, not `hasActiveUploadsAtom`, arms the `beforeunload` guard.
 */
export const clientBoundCountAtom = atom((get) =>
  CLIENT_BOUND_PHASE_LIST.reduce((total, phase) => total + get(phaseCountAtom(phase)), 0)
);

export const hasClientBoundUploadsAtom = atom((get) => get(clientBoundCountAtom) > 0);

/**
 * Scheduler signal: changes only when something the engine cares about changes. `store.sub`
 * on this pumps the scheduler on add / phase transition / retry / completion, but not on
 * progress ticks.
 */
export const schedulerSignalAtom = atom((get) => `${get(phaseCountAtom(UPLOAD_PHASE.QUEUED))}:${get(activeCountAtom)}`);

/**
 * Drives the panel header ("5 in progress") and its terminal summary states. `allSettled` is
 * the "nothing left to do" flag
 */
export const queueSummaryAtom = atom<LibraryUpload.Derived.QueueSummary>((get) => {
  const total = get(totalCountAtom);
  const inProgress = get(activeCountAtom);
  return {
    allSettled: total > 0 && inProgress === 0,
    cancelled: get(phaseCountAtom(UPLOAD_PHASE.CANCELLED)),
    done: get(phaseCountAtom(UPLOAD_PHASE.COMPLETE)),
    errored: get(phaseCountAtom(UPLOAD_PHASE.ERROR)),
    inProgress,
    total,
  };
});

/**
 * Which panel state to render. Derived here rather than in the component so the mapping can be
 * asserted without rendering. Cancelled counts as settled but not as a success, so a queue of
 * nothing but cancellations lands on `ERRORS`.
 */
export const uploadPanelStateAtom = atom<LibraryUpload.Derived.UploadPanelState>((get) => {
  const { allSettled, done, errored, total } = get(queueSummaryAtom);
  if (total === 0) return UPLOAD_PANEL_STATE.EMPTY;
  if (!allSettled) return UPLOAD_PANEL_STATE.ACTIVE;
  if (errored === 0 && done > 0) return UPLOAD_PANEL_STATE.COMPLETE;
  if (done > 0) return UPLOAD_PANEL_STATE.COMPLETE_WITH_ERRORS;
  return UPLOAD_PANEL_STATE.ERRORS;
});
